'use client';

import { redirect } from 'next/navigation';
import { ReactNode } from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Alert,
    Align,
    Box,
    BoxContent,
    Column,
    Columns,
    Link,
    Radio,
    RadioList,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    StatusState,
    Text,
    TextLevels,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { paths } from '@helpers/path';

import { ELIGIBILITY_STEP_NAME, ELIGIBILITY_STEP_STATUS, TYPE_ELIGIBILITY } from '@constants/eligibility';

import { JourneyConfigType, sendTlvLinkClick } from '@eligibility/helpers';

interface EligibilityCheckerProps {
    type: TYPE_ELIGIBILITY;
    config: JourneyConfigType;
    setType: (type: TYPE_ELIGIBILITY) => void;
    handleEligibilityChange?: (step: ELIGIBILITY_STEP_NAME) => void;
    currentStep: ELIGIBILITY_STEP_NAME;
    currentStepStatus: ELIGIBILITY_STEP_STATUS;
    isFirstStep: boolean;
    children?: ReactNode;
}

export function EligibilityChecker({
    type,
    config,
    setType,
    handleEligibilityChange: _handleEligibilityChange,
    currentStep,
    currentStepStatus,
    isFirstStep,
    children,
}: Readonly<EligibilityCheckerProps>) {
    const handleEligibilityChange = (type: TYPE_ELIGIBILITY) => {
        setType(type);
        switch (type) {
            case TYPE_ELIGIBILITY.SEARCH:
                _handleEligibilityChange?.(ELIGIBILITY_STEP_NAME.SINGLE_SEARCH_ELIG);
                break;
            case TYPE_ELIGIBILITY.PTO:
                _handleEligibilityChange?.(ELIGIBILITY_STEP_NAME.PTO_ELIG);
                break;
        }
    };

    const callTlvRedirect = () => {
        sendTlvLinkClick(config);
        redirect(paths.recall('tlvctcfai2'));
    };

    return (
        <Rows>
            <Row>
                <Box>
                    <BoxContent
                        className={classNames(currentStep === ELIGIBILITY_STEP_NAME.GMAP ? 'is-paddingless' : '')}
                    >
                        {isFirstStep && !config.eligBlock.hideEligibilityTypeSelector && (
                            <>
                                <RadioList align={Align.CENTER}>
                                    <Radio
                                        name='eligibility-type'
                                        id={TYPE_ELIGIBILITY.SEARCH}
                                        disabled={currentStepStatus === ELIGIBILITY_STEP_STATUS.LOADING}
                                        onChange={({ radioValue }) =>
                                            handleEligibilityChange(radioValue as TYPE_ELIGIBILITY)
                                        }
                                        label='Avec votre adresse'
                                        value={TYPE_ELIGIBILITY.SEARCH}
                                        checked={type === TYPE_ELIGIBILITY.SEARCH}
                                    />
                                    <Radio
                                        name='eligibility-type'
                                        id={TYPE_ELIGIBILITY.LINEAR}
                                        disabled={currentStepStatus === ELIGIBILITY_STEP_STATUS.LOADING}
                                        onChange={({ radioValue }) =>
                                            handleEligibilityChange(radioValue as TYPE_ELIGIBILITY)
                                        }
                                        value={TYPE_ELIGIBILITY.PTO}
                                        checked={type === TYPE_ELIGIBILITY.PTO}
                                        label='Avec votre prise fibre'
                                    />
                                </RadioList>
                                <Spacer size={SpacerSize.FOUR} />
                            </>
                        )}
                        {type === TYPE_ELIGIBILITY.PTO && currentStep === ELIGIBILITY_STEP_NAME.PTO_ELIG && (
                            <Columns>
                                <Column>
                                    <Text
                                        level={TextLevels.THREE}
                                        typo={TypographyAlign.TEXT_LEFT}
                                        className='has-text-centered-tablet'
                                    >
                                        Pour profiter d&apos;une installation plus rapide, retrouvez la référence sur
                                        votre prise fibre murale (armoire électrique, pièce principale...) ou sur la
                                        facture de votre opérateur internet actuel.
                                    </Text>
                                </Column>
                            </Columns>
                        )}
                        {children}
                    </BoxContent>
                </Box>
            </Row>
            {currentStep === ELIGIBILITY_STEP_NAME.GMAP &&
                currentStepStatus === ELIGIBILITY_STEP_STATUS.READY &&
                !config.eligBlock.hideTlvRecall && (
                    <Row>
                        <Alert
                            display
                            status={StatusState.INFO}
                            title='Vous ne trouvez pas votre adresse&nbsp;?'
                            description={
                                <>
                                    Vous pouvez vous{' '}
                                    <Link onClick={() => callTlvRedirect()}>faire rappeler par un conseiller</Link> pour
                                    bénéficier d&apos;un accompagnement personnalisé.
                                </>
                            }
                        />
                    </Row>
                )}
        </Rows>
    );
}
